// RITA-1526 — Vorprüfung der Logo-Datei im Browser, bevor sie hochgeladen wird.
//
// Das Backend prüft den Inhalt erneut (Magic Bytes, Größe, SVG ohne Skript, vollständige
// Dekodierung) und ist die maßgebliche Instanz; diese Prüfung spart nur den Upload einer
// offensichtlich falschen Datei und liefert dieselben verständlichen Meldungen. Zusätzlich fragt
// der Dialog den Browser, ob er die Datei wirklich als Bild anzeigen kann (Codex-Review 28.09.2026).

/** Obergrenze laut Ticket: 1 MB. */
export const CONTRACT_TYPE_LOGO_MAX_BYTES = 1024 * 1024;

/** Wert für das accept-Attribut des Dateifelds. */
export const CONTRACT_TYPE_LOGO_ACCEPT = '.png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml';

export const CONTRACT_TYPE_LOGO_MESSAGES = {
  tooLarge: 'Das Logo darf höchstens 1 MB groß sein.',
  typeNotAllowed: 'Erlaubt sind nur PNG-, JPG- oder SVG-Dateien.',
  empty: 'Die Datei ist leer.',
  unreadable: 'Die Datei ist beschädigt oder unvollständig und lässt sich nicht als Bild anzeigen.',
  saveFailed: 'Das Logo konnte nicht gespeichert werden. Bitte erneut versuchen.',
  removeFailed: 'Das Logo konnte nicht entfernt werden. Bitte erneut versuchen.',
  noPermission: 'Keine Berechtigung, Logos zu ändern.',
} as const;

const ALLOWED_MIME_TYPES = new Set(['image/png', 'image/jpeg', 'image/svg+xml']);
const ALLOWED_EXTENSION = /\.(png|jpe?g|svg)$/i;
const MIME_BY_EXTENSION: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  svg: 'image/svg+xml',
};

/**
 * MIME-Typ für die Vorschau: der gemeldete Typ, wenn er erlaubt ist, sonst der aus der Endung.
 * Eine SVG-Datei ohne Typ würde als Objekt-URL im <img> sonst nicht gezeichnet.
 */
export const contractTypeLogoMimeTypeFor = (file: { name: string; type: string }): string => {
  if (ALLOWED_MIME_TYPES.has(file.type)) return file.type;
  const match = ALLOWED_EXTENSION.exec(file.name);
  return match ? MIME_BY_EXTENSION[match[1].toLowerCase()] : file.type;
};

export type ContractTypeLogoFileCheck = { ok: true } | { ok: false; message: string };

/** Nur die drei Merkmale, die der Browser sicher liefert; ein echtes File erfüllt sie. */
export interface ContractTypeLogoFileLike {
  name: string;
  type: string;
  size: number;
}

/**
 * Typ nach MIME-Typ des Browsers ODER nach Dateiendung (manche Systeme melden keinen oder einen
 * ungewöhnlichen Typ wie image/x-png), dann leer, dann Größe. Den Inhalt prüft das Backend.
 */
export const checkContractTypeLogoFile = (file: ContractTypeLogoFileLike): ContractTypeLogoFileCheck => {
  const typeAllowed = ALLOWED_MIME_TYPES.has(file.type) || ALLOWED_EXTENSION.test(file.name);
  if (!typeAllowed) return { ok: false, message: CONTRACT_TYPE_LOGO_MESSAGES.typeNotAllowed };
  if (file.size === 0) return { ok: false, message: CONTRACT_TYPE_LOGO_MESSAGES.empty };
  if (file.size > CONTRACT_TYPE_LOGO_MAX_BYTES) return { ok: false, message: CONTRACT_TYPE_LOGO_MESSAGES.tooLarge };
  return { ok: true };
};

/**
 * Fragt den Browser, ob er die gewählte Datei tatsächlich als Bild anzeigen kann: eine Datei mit
 * gültigem Kopf, aber beschädigtem Inhalt, zeigt <img> sonst nur als leeres Symbol. decode() wartet
 * auf die vollständige Dekodierung; Browser ohne decode() melden über load/error. Ohne Bild-API gilt
 * die Datei als anzeigbar — das Backend prüft ohnehin vollständig. In jsdom (Unit-Tests) gibt es
 * zwar `Image`, aber weder decode() noch load/error-Ereignisse: dort bliebe die Prüfung offen, deshalb
 * wird sie in den Dialog-Tests gemockt.
 */
export const checkContractTypeLogoDisplayable = (url: string): Promise<boolean> =>
  new Promise((resolve) => {
    if (typeof Image === 'undefined') {
      resolve(true);
      return;
    }
    const image = new Image();
    image.onload = () => resolve(true);
    image.onerror = () => resolve(false);
    image.src = url;
    if (typeof image.decode === 'function') {
      image.decode().then(
        () => resolve(true),
        () => resolve(false),
      );
    }
  });

/** Größe für die Anzeige neben dem Dateinamen, z. B. „12 kB“ oder „0,9 MB“. */
export const formatContractTypeLogoSize = (bytes: number): string => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} kB`;
};

/** Fehlercodes, deren Backend-Meldung für Anwender gedacht ist und unverändert angezeigt wird. */
export const CONTRACT_TYPE_LOGO_USER_FACING_CODES = [
  'LOGO_TOO_LARGE',
  'LOGO_TYPE_NOT_ALLOWED',
  'LOGO_UNREADABLE',
  'NOT_FOUND',
] as const;
/** Codes des Berechtigungsfehlers (accessCheck wirft AuthenticationError → UNAUTHENTICATED). */
const PERMISSION_CODES = ['UNAUTHENTICATED', 'FORBIDDEN'];

/**
 * Meldung aus einem Fehler der Mutation: die vom Backend formulierte Meldung, wenn der
 * GraphQL-Fehler einen der Anwender-Codes trägt; sonst der übergebene Ersatztext (technische
 * Meldungen wie „Forbidden“ oder Datenbankfehler bleiben außen vor). Ohne instanceof, damit
 * auch gemockte Clients funktionieren.
 */
export const contractTypeLogoErrorMessage = (error: unknown, fallback: string): string => {
  const graphQLErrors = (error as { graphQLErrors?: { message?: string; extensions?: { code?: unknown } }[] } | null)
    ?.graphQLErrors;
  const first = graphQLErrors && graphQLErrors.length > 0 ? graphQLErrors[0] : undefined;
  const code = first?.extensions?.code;
  if (typeof code === 'string' && PERMISSION_CODES.includes(code)) return CONTRACT_TYPE_LOGO_MESSAGES.noPermission;
  const userFacing =
    typeof code === 'string' && (CONTRACT_TYPE_LOGO_USER_FACING_CODES as readonly string[]).includes(code);
  return userFacing && first?.message && first.message.trim() ? first.message : fallback;
};
